<template>
    <validation-observer v-if="!loading" v-slot="{ validate }" slim>
        <form @submit.prevent="update(validate)" class="mb-4 p-2">
            <div class="row">
                <div class="col card m-2 p-1">
                    <div v-if="form.confirmed_at">
                        <app-button native-type="button" type="danger" @click="confirmBooking(form.id, false)" small>
                            {{ $t('Unconfirm') }}
                        </app-button>
                        <router-link v-if="$store.getters.canEditContracts && !form.contract"
                                     :to="{name: 'contracts.create', params: {booking_id: form.id}}"
                                     class="btn btn-success btn-sm text-white">
                            {{ $t('Create contract') }}
                        </router-link>
                        <router-link v-else-if="form.contract && $store.getters.canViewContracts"
                                     :to="{name: 'contracts.show', params: {contract_id: form.contract.id}}"
                                     class="btn btn-info btn-sm ml-2 text-white">
                            {{ $t('Contract') }}
                        </router-link>
                        <app-loader-gif v-if="vehicle_loading"/>
                        <app-back-button right/>
                        {{ $t('Confirmed') }}: {{ form.confirmed_at | formatDateTime }}
                        <template v-if="form.early">
                            <span class="badge badge-warning text-uppercase ml-2">
                                {{ $t('Early return') }}
                            </span>
                        </template>
                    </div>
                    <div v-else>
                        <app-button native-type="button" type="success" @click="confirmBooking(form.id, true)" small>
                            {{ $t('Confirm') }}
                        </app-button>
                        <app-button :loading="loading" small>
                            {{ $t('Update') }}
                        </app-button>
                        <app-loader-gif v-if="vehicle_loading"/>
                        <app-back-button right/>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col card p-3 mx-2">
                    <div class="d-flex justify-content-between">
                        <h4>{{ $t('Vehicle') }}</h4>
                        <router-link :to="{name: 'vehicles.show', params: {vehicle_id: form.vehicle_id}}" class="btn btn-info btn-sm text-white">
                            {{ $t('View') }}
                        </router-link>
                    </div>
                    <app-select
                        id="vehicle_id"
                        v-model="form.vehicle_id"
                        :options="vehicles.map(c => c.id)"
                        rules="required"
                        name="vehicle_id"
                        openDirection="bottom"
                        searchable
                        not-empty
                        closeOnSelect
                        :disabled="form.early"
                        @input="id => getPrice(id, false)"
                        :custom-label="id => getVehicleName(id)">
                        <template #label>
                            {{ $t('Vehicle') }}*
                        </template>
                    </app-select>
                    <app-overlapping :vehicle="form.vehicle"/>
                    <app-datepicker id="search_dates"
                                    v-model="search_dates"
                                    range
                                    name="search_dates"
                                    class="mb-1"
                                    :disabled="form.early"
                                    @change="getPrice(form.vehicle_id, true)"
                                    rules="required">
                        <template #label>
                            {{ $t('Start date') }} & {{ $t('Return date') }}*
                        </template>
                    </app-datepicker>
                    <div class="row">
                        <div class="col">
                            <app-datepicker id="start_time"
                                            v-model="start_time"
                                            inputType="time"
                                            name="start_time"
                                            class="mb-1"
                                            :disabled="form.early"
                                            @change="getPrice(form.vehicle_id, true)"
                                            rules="required">
                                <template #label>
                                    {{ $t('Start time') }}*
                                </template>
                            </app-datepicker>
                        </div>
                        <div class="col">
                            <app-datepicker id="end_time"
                                            v-model="end_time"
                                            inputType="time"
                                            name="end_time"
                                            class="mb-1"
                                            :disabled="form.early"
                                            @change="getPrice(form.vehicle_id, true)"
                                            rules="required">
                                <template #label>
                                    {{ $t('Return time') }}*
                                </template>
                            </app-datepicker>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <app-select
                                id="pickup_location_id"
                                v-model="form.pickup_location_id"
                                :options="locations.map(c => c.id)"
                                rules="required"
                                name="pickup_location_id"
                                openDirection="bottom"
                                searchable
                                not-empty
                                closeOnSelect
                                :disabled="form.early"
                                @input="checkPaidServices"
                                :custom-label="id =>locations.some(c => c.id === id) ? locations.find(c => c.id === id).name[locale] : null">
                                <template #label>
                                    {{ $t('Pick up from') }}*
                                </template>
                            </app-select>
                            <app-input v-if="form.pickup_location_id === otherLocationId"
                                       class="mt-2"
                                       id="pickup_location"
                                       v-model="form.pickup_location_custom"
                                       :placeholder="`${$t('Address')}*`"
                                       :disabled="form.early"
                                       @blur="checkPaidServices"
                                       name="pickup_location"
                                       rules="required" />
                        </div>
                        <div class="col">
                            <app-select
                                id="return_location_id"
                                v-model="form.return_location_id"
                                :options="locations.map(c => c.id)"
                                rules="required"
                                name="return_location_id"
                                openDirection="bottom"
                                searchable
                                not-empty
                                closeOnSelect
                                :disabled="form.early"
                                @input="checkPaidServices"
                                :custom-label="id => locations.some(c => c.id === id) ? locations.find(c => c.id === id).name[locale] : null">
                                <template #label>
                                    {{ $t('Return to') }}*
                                </template>
                            </app-select>
                            <app-input v-if="form.return_location_id === otherLocationId"
                                       class="mt-2"
                                       id="return_location"
                                       v-model="form.return_location_custom"
                                       :placeholder="`${$t('Address')}*`"
                                       :disabled="form.early"
                                       @blur="checkPaidServices"
                                       name="return_location"
                                       rules="required" />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <app-textarea id="notes" v-model="form.notes" name="notes" :rows="3" :disabled="form.early">
                                <template #label>
                                    {{ $t('Internal notes') }}
                                </template>
                            </app-textarea>
                        </div>
                    </div>
                    <div class="mt-2 d-flex justify-content-start">
                        <app-custom-checkbox id="travel_out_check" v-model="travel_out_check" name="travel_out_check" :disabled="form.early">
                            {{ $t('Travel out of Estonia') }}
                        </app-custom-checkbox>
                    </div>
                    <app-input v-if="travel_out_check" id="travel_out" v-model="form.travel_out" name="travel_out" rules="required" class="mt-2" :disabled="form.early">
                        <template #label> {{ $t('Countries') }}*</template>
                    </app-input>
                    <div class="row mt-2">
                        <div class="col-md-6 col-sm-12">
                            <div class="accordion" id="ad_eqs">
                                <div class="card shadow-none">
                                    <div class="card-header pointer accordeon-border" data-toggle="collapse" href="#additional_equipment">
                                        <a class="card-link font-weight-normal pl-1">
                                            {{ $t('Additional eq') }}
                                            <fa-icon :icon="['fas', 'angle-down']" pull="right"/>
                                        </a>
                                    </div>
                                    <div id="additional_equipment" class="collapse show pt-3" aria-labelledby="headingL-varustus" data-parent="#ad_eqs">
                                        <app-loader-gif v-if="loading"/>
                                        <template v-if="additions.length > 0">
                                            <app-custom-checkbox v-for="(eq, j) in additions"
                                                                 :id="`eq_${j}`"
                                                                 :name="`eq_${j}`"
                                                                 :key="j"
                                                                 :disabled="form.early"
                                                                 :value="form.additional_eq.map(e => e.id).includes(eq.id)"
                                                                 @input="addAddEq(eq)">
                                                {{ $t(eq.name[locale]) }}
                                                <small v-if="eq.all_prices && eq.all_prices.price">
                                                    ({{ ((eq.all_prices.price + eq.all_prices.added_price) / 100).toFixed(2) }} EUR)
                                                </small>
                                            </app-custom-checkbox>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <div class="accordion" id="services_acc">
                                <div class="card shadow-none">
                                    <div class="card-header pointer accordeon-border" data-toggle="collapse" href="#services">
                                        <a class="card-link font-weight-normal pl-1">
                                            {{ $t('Services') }}
                                            <fa-icon :icon="['fas', 'angle-down']" pull="right"/>
                                        </a>
                                    </div>
                                    <div id="services" class="collapse show" aria-labelledby="headingL-varustus" data-parent="#services_acc">
                                        <div class="card-body">
                                            <template v-if="form.services && form.services.length">
                                                <template v-for="(serv, s) in form.services">
                                                    <div class="d-flex justify-content-between" :key="`s_${s}`">
                                                        <p class="mb-1">
                                                            {{ serv.name[locale] }}, <strong>{{ (serv.price / 100).toFixed(2) }}€</strong>
                                                        </p>
                                                        <app-button v-if="form.edit_price" native-type="button" type="danger" @click="removeService(s)" :disabled="form.early" xsmall>
                                                            {{ $t('Delete') }}
                                                        </app-button>
                                                    </div>
                                                </template>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col card p-3 mx-2">
                    <h4>{{ $t('Rentee') }}</h4>
                    <div class="row">
                        <div class="col-md-6">
                            <app-input id="firstName" v-model="form.fname" name="firstName" rules="required" :disabled="form.early">
                                <template #label> {{ $t('First name') }}* </template>
                            </app-input>
                        </div>
                        <div class="col-md-6">
                            <app-input id="lastName" v-model="form.lname" name="lastName" rules="required" :disabled="form.early">
                                <template #label> {{ $t('Last name') }}* </template>
                            </app-input>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <app-input id="phone" type="tel" v-model="form.phone" name="phone" @blur="checkClientDiscount" rules="required" :disabled="form.early">
                                <template #label> {{ $t('Phone') }}* </template>
                            </app-input>
                        </div>
                        <div class="col-md-6">
                            <app-input id="email" v-model="form.email" name="email" @blur="checkClientDiscount" :disabled="form.early">
                                <template #label> {{ $t('Email') }} </template>
                            </app-input>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-lg-6 col-md-6">
                            <app-textarea id="comments" v-model="form.comments" name="comments" :rows="3" :disabled="form.early">
                                <template #label>
                                    {{ $t('Pick up & return info') }}
                                </template>
                            </app-textarea>
                        </div>
                        <div class="col-lg-6 col-md-6">
                            <app-select
                                id="lang"
                                v-model="form.lang"
                                :options="langs.map(l => l.key)"
                                name="lang"
                                openDirection="bottom"
                                rules="required"
                                class="mb-3"
                                searchable
                                not-empty
                                closeOnSelect
                                :disabled="form.early"
                                :custom-label="
                                    key =>
                                        langs.some(l => l.key === key)
                                            ? $t(langs.find(l => l.key === key).name)
                                            : null
                                ">
                                <template #label>
                                    {{ $t('Language of communication') }}*
                                </template>
                            </app-select>
                            <div class="mt-2 d-flex justify-content-start">
                                <app-custom-checkbox id="newsletter" v-model="form.newsletter" name="newsletter" :disabled="form.early">
                                    {{ $t('Newsletter') }}
                                </app-custom-checkbox>
                            </div>
                        </div>
                    </div>
                    <hr>
                    <div class="d-flex justify-content-between">
                        <h4>{{ $t('Rental prices') }}</h4>
                        <div>
                            <app-radio
                                id="edit_price_no"
                                v-model="form.edit_price"
                                name="edit_price_no"
                                :label="$t('Automatically calculated price')"
                                :val="false"
                                inline />
                            <app-radio
                                id="edit_price_yes"
                                v-model="form.edit_price"
                                name="edit_price_yes"
                                :label="$t('Manually added price')"
                                :val="true"
                                inline />
                        </div>
                    </div>
                    <div class="row">
                        <div v-if="!form.edit_price" class="col">
                            <div class="row">
                                <div class="col">
                                    <div class="form-group mb-1">
                                        <label class="mb-0">{{ $t('Days') }}</label>
                                        <p class="custom-gray-input-bg">{{ form.prices.days }}</p>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="form-group mb-1">
                                        <label class="mb-0">
                                            {{ $t('Day price') }}
                                        </label>
                                        <p class="custom-gray-input-bg">
                                            {{ form.prices.avg_price }} €
                                        </p>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="form-group mb-1">
                                        <label class="mb-0">{{ $t('Eqipment price') }}</label>
                                        <p class="custom-gray-input-bg">{{ form.prices.eq_total }} €</p>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="form-group mb-1">
                                        <label class="mb-0">{{ $t('Services') }}</label>
                                        <p class="custom-gray-input-bg">{{ form.prices.service_total }} €</p>
                                    </div>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col">
                                    <div class="form-group mb-1">
                                        <label class="mb-0">{{ $t('Extra hours') }}</label>
                                        <p class="custom-gray-input-bg">{{ form.prices.over_hrs }}</p>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="form-group mb-1">
                                        <label class="mb-0">{{ $t('Extra hour price') }}</label>
                                        <p class="custom-gray-input-bg">{{ form.prices.hr_price }} €</p>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="form-group mb-1">
                                        <label class="mb-0">{{ $t('Bond') }}</label>
                                        <p class="custom-gray-input-bg">{{ form.prices.bond }} €</p>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="form-group mb-1">
                                        <label class="mb-0">{{ $t('Total price') }}</label>
                                        <p class="custom-gray-input-bg">{{ form.prices.total }} €</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="col">
                            <div class="row">
                                <div class="col">
                                    <app-input id="days" v-model="form.prices.days" name="days" rules="required" type="number" :step="'0.01'">
                                        <template #label>{{ $t('Days') }} *</template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="avg_price" v-model="form.prices.avg_price" name="avg_price" rules="required" type="number" :step="'0.01'">
                                        <template #label>
                                            {{ $t('Day price') }} *
                                        </template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="eq_total" v-model="form.prices.eq_total" name="eq_total" rules="required" type="number" :step="'0.01'">
                                        <template #label>{{ $t('Eqipment price') }} *</template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="service_total" v-model="form.prices.service_total" name="service_total" rules="required" type="number" :step="'0.01'">
                                        <template #label>{{ $t('Services') }} *</template>
                                    </app-input>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col">
                                    <app-input id="over_hrs" v-model="form.prices.over_hrs" name="over_hrs" type="number">
                                        <template #label>{{ $t('Extra hours') }}</template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="hr_price" v-model="form.prices.hr_price" name="hr_price" type="number" :step="'0.01'">
                                        <template #label>{{ $t('Extra hour price') }}</template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="bond" v-model="form.prices.bond" name="bond" rules="required" type="number" :step="'0.01'">
                                        <template #label>{{ $t('Bond') }} *</template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="total" v-model="form.prices.total" name="total" rules="required" type="number" :step="'0.01'" disabled>
                                        <template #label>{{ $t('Total price') }}</template>
                                    </app-input>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p v-if="form.edit_price" class="text-danger text-center">
                        {{ $t('Discounts and promos will NOT be subtracted') }}
                    </p>
                    <div v-if="form.early || form.early_return_location_id || form.early_return_at" class="row">
                        <div class="col">
                            <app-datepicker id="early_return_at"
                                            v-model="form.early_return_at"
                                            name="early_return_at"
                                            inputType="datetime"
                                            class="mb-1"
                                            rules="required">
                                <template #label>
                                    {{ $t('Early return date & time') }}*
                                </template>
                            </app-datepicker>
                        </div>
                        <div class="col">
                            <app-select
                                id="early_return_location_id"
                                v-model="form.early_return_location_id"
                                :options="locations.map(c => c.id)"
                                rules="required"
                                name="early_return_location_id"
                                openDirection="bottom"
                                searchable
                                not-empty
                                closeOnSelect
                                @input="checkPaidServices"
                                :custom-label="id => locations.some(c => c.id === id) ? locations.find(c => c.id === id).name[locale] : null">
                                <template #label>
                                    {{ $t('Early return to') }}*
                                </template>
                            </app-select>
                        </div>
                    </div>
                    <div class="row mb-1">
                        <div class="col">
                            <template v-if="form.promo">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Promocode') }}</label>
                                    <p class="custom-gray-input-bg">"{{ form.promo.code }}" <small>-{{ form.promo.discount_percent }}%</small></p>
                                </div>
                            </template>
                            <template v-else-if="form.client_id !== null">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Client') }}</label>
                                    <p class="custom-gray-input-bg">{{ form.client.name }} <small>-{{ form.client.discount_percent }}%</small></p>
                                </div>
                            </template>
                        </div>
                    </div>
                    <div class="form-group">
                        <app-button :loading="loading" small>
                            {{ $t('Update') }}
                        </app-button>
                    </div>
                </div>
            </div>
        </form>
    </validation-observer>
    <app-loader-svg v-else/>
</template>

<script>
import BookingService from '@services/booking.service';
import {mapGetters} from 'vuex';
import AdditionService from '@services/addition.service';
import LocationService from '@services/location.service';
import VehicleService from '@services/vehicle.service';
import moment from 'moment';
import PublicBookingService from '@services/publicBooking.service';
import PublicSettingsService from '@services/publicSettings.service';

export default {
    data() {
        return {
            search_dates: [],
            start_time: '',
            end_time: '',
            form: {
                early_return_at: null,
                early_return_location_id: null,
                client: {
                    bond: false
                },
                prices: {},
                edit_price: false,
                start_date: null,
                end_date: null,
                start_time: null,
                end_time: null,
                additional_eq: [],
                services: [],
                early: false,
                newsletter: false,
                vehicle: {
                    overlapping: {
                        start: null,
                        end: null
                    }
                },
            },
            vehicles: [],
            additions: [],
            locations: [],
            loading: false,
            dont_edit_prices: true,
            promocode: null,
            travel_out_check: false,
            vehicle_loading: false,
            discount: 0
        };
    },
    watch: {
        locale() {
            this.getBooking(this.$route.params.booking_id);
        },
        // 'form.edit_price'(val) {
        //     if (val === true) {
        //         this.form.prices.total = this.form.prices.total / 100;
        //     } else {
        //         this.form.prices.total = this.form.prices.total * 100;
        //     }
        // }
    },
    computed: {
        ...mapGetters(['locale', 'langs', 'localeDatetimeFormat', 'localeDateFormat', 'otherLocationId'])
    },
    methods: {
        removeService(index) {
            this.form.services.splice(index, 1);
        },
        checkPaidServices() {
            this.checkIsVehicleBooked();
            if (!this.start_time || !this.end_time || !this.search_dates || !this.form.pickup_location_id || !this.form.return_location_id || !this.form.vehicle_id) {
                return false;
            }
            if (this.form.edit_price) {
                return false;
            }
            const queryObj = {
                start_date: this.search_dates[0],
                end_date: this.search_dates[1],
                start_time: this.start_time,
                end_time: this.end_time,
                pickup_location_id: this.form.pickup_location_id,
                pickup_custom: this.form.pickup_location_id === this.otherLocationId ? 1 : 0,
                return_location_id: this.form.return_location_id,
                return_custom: this.form.return_location_id === this.otherLocationId ? 1 : 0,
            };

            PublicBookingService.checkPaidServices(this.form.vehicle_id, queryObj).then(res => {
                let services = res;
                if (this.form.services.length && services.length) {
                    let existingServiceTotal = 0;
                    this.form.services.map(s => existingServiceTotal += s.price);
                    this.form.services = [];
                    this.form.prices.service_total = 0;
                    this.form.prices.total = parseFloat(this.form.prices.total) - (existingServiceTotal / 100);
                }
                this.form.services = services;
                if(services.length === 0) {
                    this.form.prices.total = (parseFloat(this.form.prices.total) - parseFloat(this.form.prices.service_total)).toFixed(2);
                    this.form.prices.service_total = 0;
                } else {
                    let serviceTotal = 0;
                    services.map(s => serviceTotal += s.price);
                    this.form.prices.service_total = serviceTotal / 100;
                    this.form.prices.total = (parseFloat(this.form.prices.total) + parseFloat(this.form.prices.service_total)).toFixed(2);
                }
            });
        },
        checkClientDiscount() {
            const queryObj = {
                phone: this.form.phone,
                email: this.form.email
            };
            PublicBookingService.checkClientDiscount(queryObj).then(res => {
                if (res) {
                    this.discount = res.discount;
                    this.form.client_id = res.id;
                    this.form.promo_id = null;
                    this.promocode = null;
                } else {
                    this.discount = 0;
                    this.promocode = null;
                    this.form.client_id = null;
                    this.checkPromo();
                }
            });
        },
        checkPromo() {
            if (!this.search_dates.length) {
                return false;
            }
            const queryObj = {
                code: this.promocode,
                start_date: this.search_dates[0],
                end_date: this.search_dates[1]
            };
            PublicBookingService.checkPromocode(queryObj).then(res => {
                if (res) {
                    this.discount = res.discount;
                    this.form.promo_id = res.id;
                    this.promocode = res.code;
                    this.form.client_id = null;
                } else {
                    this.discount = 0;
                    this.promocode = null;
                    this.form.promo_id = null;
                }
            });
        },
        checkIsVehicleBooked() {
            if (!this.start_time || !this.end_time || !this.search_dates || !this.form.vehicle_id) {
                return false;
            }
            const queryObj = {
                start_date: this.search_dates[0],
                end_date: this.search_dates[1],
                start_time: this.start_time,
                end_time: this.end_time,
                vehicle_id: this.form.vehicle_id,
                booking_id: this.$route.params.booking_id,
            };

            PublicBookingService.checkIsVehicleBooked(queryObj);
        },
        addAddEq(eq) {
            const ids = this.form.additional_eq.map(e => e.id);
            const index = ids.indexOf(eq.id);
            if (index > -1) {
                this.form.additional_eq.splice(index, 1);
            } else {
                this.form.additional_eq.push(eq);
            }
        },
        getVehicleName(id) {
            const vehicle = this.vehicles.some(c => c.id === id) ? this.vehicles.find(c => c.id === id) : null;
            if (vehicle) {
                return `${vehicle.make.name} ${vehicle.model.name} ${vehicle.slug.slice(-1)}, ${vehicle.reg_no}`;
            }
            return vehicle;
        },
        getVehicles() {
            VehicleService.noEmpties().then(res => {
                this.vehicles = res;
                this.getPrice(this.form.vehicle_id, false);
            });
        },
        getLocations() {
            LocationService.locations().then(res => this.locations = res);
        },
        update(validate) {
            validate().then(valid => {
                if (valid) {
                    this.loading = true;
                    this.form.start_date = this.search_dates[0];
                    this.form.end_date = this.search_dates[1];
                    this.form.start_time = this.start_time;
                    this.form.end_time = this.end_time;
                    this.form.dont_edit_prices = this.form.edit_price;
                    this.form.pickup_custom = this.form.pickup_location_id === this.otherLocationId ? 1 : 0;
                    this.form.return_custom = this.form.return_location_id === this.otherLocationId ? 1 : 0;

                    if (this.form.edit_price) {
                        this.setManualPrices();
                    }
                    BookingService.update(this.$route.params.booking_id, this.form).then(res => {
                        this.form = res;
                        if (this.$route.query.early === 'true') {
                            this.form.early = true;
                        }
                        const promoDiscount = res.promo_id ? res.promo.discount_percent : 0;
                        const clientDiscount = res.client_id ? res.client.discount_percent : 0;
                        this.discount = Math.max(clientDiscount, promoDiscount) || 0;
                        this.setPrices(res.prices);
                        this.search_dates = [moment(res.start_time).format(this.localeDateFormat), moment(res.end_time).format(this.localeDateFormat)];
                        this.start_time = moment(res.start_time).format('HH:mm');
                        this.end_time = moment(res.end_time).format('HH:mm');
                        this.form.early_return_at = res.early_return_at ? moment(res.early_return_at).format('DD.MM.YYYY HH:mm') : null;
                        this.$toastr.s(this.$t('Updated'));
                        this.loading = false;
                        // this.$router.push({name: 'booking.show', params: {booking_id: res.id}});
                    }).catch(e => this.loading = false);
                }
            });
        },
        getPrice(vehicleId, setPrice) {
            if (!this.form.vehicle_id) {
                return false;
            }
            if (!this.search_dates.length) {
                return false;
            }
            const vehicle = this.vehicles.find(veh => veh.id === vehicleId);
            if (!vehicle) {
                return false;
            }
            const queryObj = {
                vehicle_id: vehicle.id,
                client_id: this.form.client_id,
                slug: vehicle.slug,
                discount: this.discount,
                start_date: this.search_dates[0],
                end_date: this.search_dates[1],
                additional_eq: this.form.additional_eq,
                pickup_location_id: this.form.pickup_location_id,
                pickup_custom: this.form.pickup_location_id === this.otherLocationId ? 1 : 0,
                return_location_id: this.form.return_location_id,
                return_custom: this.form.return_location_id === this.otherLocationId ? 1 : 0,
            };
            if(this.start_time) {
                queryObj.start_time = this.start_time;
            }
            if(this.end_time) {
                queryObj.end_time = this.end_time;
            }

            this.getVehicle(queryObj, setPrice);
            this.getAddEqPrices(queryObj);
            this.checkPaidServices();
        },
        getVehicle(queryObj, setPrice) {
            this.vehicle_loading = true;
            PublicBookingService.getVehicle(queryObj).then(res => {
                let prices = [];
                if (!setPrice) {
                    prices = this.form.prices;
                }
                this.form.vehicle = res.vehicle;
                if (!this.form.edit_price) {
                    this.form.services = res.vehicle.services;
                }
                this.form.vehicle_id = res.vehicle.id;
                if (!this.form.edit_price && setPrice) {
                    this.setPrices(res.vehicle.all_prices);
                }
                this.checkIsVehicleBooked();
                if (!setPrice) {
                    this.form.prices = prices;
                }
                this.vehicle_loading = false;
            });
        },
        getBooking(bookingId) {
            this.loading = true;
            BookingService.booking(bookingId).then(res => {
                this.form = res;
                this.form.early_return_at = res.early_return_at ? moment(res.early_return_at).format('DD.MM.YYYY HH:mm') : null;
                this.form.early_return_location_id = res.early_return_location_id;
                if (this.$route.query.early === 'true') {
                    this.form.early = true;
                }
                if (this.form.client) {
                    this.discount = this.form.client && this.form.client.discount_percent;
                }
                if (this.form.promo) {
                    this.discount = this.form.promo && this.form.promo.discount_percent;
                    this.promocode = this.form.promo.code;
                }
                this.setPrices(res.prices);
                this.search_dates = [moment(res.start_time).format(this.localeDateFormat), moment(res.end_time).format(this.localeDateFormat)];
                this.start_time = moment(res.start_time).format('HH:mm');
                this.end_time = moment(res.end_time).format('HH:mm');
                this.loading = false;
            });
        },
        setManualPrices() {
            console.log('setManualPrices');
            this.form.prices.eq_total = this.form.prices.eq_total * 100;
            this.form.prices.service_total = this.form.prices.service_total * 100;
            this.form.prices.avg_price = this.form.prices.avg_price * 100;
            this.form.prices.bond = this.form.client && this.form.client.bond === false ? 0 : this.form.prices.bond * 100;
            this.form.prices.hr_price = this.form.prices.hr_price * 100;
        },
        setPrices(prices) {
            // console.log('setPrices');

            this.form.prices.eq_total = (prices.eq_total / 100).toFixed(2);
            this.form.prices.service_total = (prices.service_total / 100).toFixed(2);
            this.form.prices.avg_price = (prices.avg_price / 100).toFixed(2);
            this.form.prices.days = prices.days;
            this.form.prices.bond = this.form.client !== null && this.form.client.bond === false ? 0 : prices.bond / 100;
            this.form.prices.total = (parseInt(prices.total) / 100).toFixed(2);
            this.form.prices.hr_price = (prices.hr_price / 100).toFixed(2);
            this.form.prices.over_hrs = prices.over_hrs;

            // if (this.form.edit_price) {
            //     this.form.prices.total = prices.total;
            // }
        },
        getAddEqPrices(queryObj) {
            PublicSettingsService.addEq(queryObj).then(res => {
                this.additions = res;
            });
        },
        confirmBooking(bookingId, confirm) {
            let msg = this.$t('Unconfirm');
            if (confirm) {
                msg = this.$t('Confirm');
            }
            if (!window.confirm(`${msg}?`)) {
                return false;
            }
            BookingService.confirmBooking(bookingId).then(res => {
                this.form = res;
                this.setPrices(res.prices);
                this.form.search_dates = [moment(res.start_time).format(this.localeDatetimeFormat), moment(res.end_time).format(this.localeDatetimeFormat)];
                this.$toastr.s(this.$t('Updated'));
            });
        },
    },
    created() {
        this.getBooking(this.$route.params.booking_id);
        this.getLocations();
        this.getVehicles();
        if (this.$route.query.early === 'true') {
            this.form.early = true;
        }
    }
};
</script>
